import React, { useState, memo } from 'react';
import cn from 'classnames';

import {
  SortableContainer,
  SortableElement,
  SortableContainerProps,
  SortableElementProps,
  SortableHandle,
  SortStartHandler,
} from 'react-sortable-hoc';
import { Icon } from 'components';
import { Col } from 'components/layout';
import { AddTitleValueEmoji } from 'components/TitleValueEmojiV2';
import styles from 'components/DragAndDrop/DragAndDrop.module.scss';
import { CustomLinksAttributes } from 'lib/models/custom-links';

interface ISortableHandleElement {
  children: React.ReactNode;
  className: string;
}

interface ISortableItem extends SortableElementProps {
  children: React.ReactNode;
  className?: string;
}

interface ISortableContainer extends SortableContainerProps {
  children: React.ReactNode;
  className: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DndTrigger: React.ComponentClass<ISortableHandleElement, any> =
  SortableHandle(
    ({
      children,
      className,
    }: {
      children: React.ReactNode;
      className: string;
    }) => <div className={className}>{children}</div>
  );

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DndItem: React.ComponentClass<ISortableItem, any> = SortableElement(
  ({ children }: { children: React.ReactNode }) => <div>{children}</div>
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DndList: React.ComponentClass<ISortableContainer, any> =
  SortableContainer(
    ({
      children,
      className,
    }: {
      children: React.ReactNode;
      className: string;
    }) => {
      return <div className={className}>{children}</div>;
    }
  );
export interface DragAndDropProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
  fields: CustomLinksAttributes[];
  updateCustomLink: (
    index: number,
    data: {
      title?: string;
      name?: string;
      link?: string;
      value?: string;
      emoji?: string;
    }
  ) => void;
  remove: (index: number) => void;
  name: string;
  move: (oldIndex: number, newIndex: number) => void;
}

export const DragAndDrop: React.FC<DragAndDropProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
    fields,
    updateCustomLink,
    remove,
    name,
    move,
  }: DragAndDropProps) => {
    const contentClassNames = cn(
      {
        [styles.DragAndDrop]: true,
      },
      className
    );

    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
    const [dragItemIndex, setDragItemIndex] = useState<number | null>(null);

    const onSortStart: SortStartHandler = ({ index }) => {
      setDragItemIndex(index);
      document.body.style.cursor = 'grabbing';
    };

    const onSortEnd = ({
      oldIndex,
      newIndex,
    }: {
      oldIndex: number;
      newIndex: number;
    }): void => {
      setDragItemIndex(null);
      document.body.style.cursor = 'default';
      move(oldIndex, newIndex);
    };

    return (
      <div
        className={contentClassNames}
        style={style}
        data-testid="DragAndDrop"
      >
        <DndList
          lockAxis="y"
          lockToContainerEdges={true}
          useDragHandle
          onSortStart={onSortStart}
          onSortEnd={onSortEnd}
          className={styles.DragAndDropContainer}
          helperClass={styles.DragAndDropContainer}
        >
          {fields.map((item, index) => {
            const showControls =
              dragItemIndex === null
                ? hoveredIndex === index
                : dragItemIndex === index;
            return (
              <DndItem key={item.id} index={index}>
                <div
                  className={styles.textareaContainer}
                  onMouseEnter={() => {
                    const index = fields.findIndex(
                      (field) => field.id === item.id
                    );
                    setHoveredIndex(index);
                  }}
                  onMouseLeave={() => {
                    setHoveredIndex(null);
                  }}
                >
                  <DndTrigger className={styles.drag}>
                    {showControls ? (
                      <Icon iconName="drag-and-drop" size="small" />
                    ) : (
                      <div className={styles.wrapper}></div>
                    )}
                  </DndTrigger>
                  <Col
                    span={24}
                    key={item?.id}
                    className={styles.sectionRowCol}
                    style={{ padding: 0 }}
                  >
                    <AddTitleValueEmoji
                      isCustomLink={true}
                      onRemove={() => remove(index)}
                      index={index}
                      name={name}
                      onUpdate={updateCustomLink}
                      showControls={showControls}
                    />
                  </Col>
                </div>
                <div className={styles.gapInput}></div>
              </DndItem>
            );
          })}
        </DndList>
      </div>
    );
  }
);

DragAndDrop.displayName = 'DragAndDrop';
